import { useEffect, useRef, useState, FC, useCallback } from "react";
import Hls from "hls.js";
import styles from "./VideoPlayer.module.css";
// import debugService from "@api/debugService";
import { Video } from "@types";
import { ThumbnailGenerator } from "@utils/ThumbnailGenerator";

interface VideoPlayerProps {
  video: Video;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  onError?: (error: Error) => void;
  onReady?: () => void;
  onEnded?: () => void;
  className?: string;
}

export const VideoPlayer: FC<VideoPlayerProps> = ({
  video,
  autoPlay = true,
  muted = false,
  loop = false,
  onError,
  onReady,
  onEnded,
  className = "",
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const qualityMenuRef = useRef<HTMLDivElement>(null);
  const qualityButtonRef = useRef<HTMLButtonElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [currentQuality, setCurrentQuality] = useState<number>(-1);
  const [qualityLevels, setQualityLevels] = useState<
    { value: number; label: string }[]
  >([]);
  const [showQualityMenu, setShowQualityMenu] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(false);
  const preventVideoClickRef = useRef(false); // Референс для отслеживания необходимости предотвращения клика

  // Determine video orientation
  const isVerticalVideo =
    video.width && video.height ? video.width / video.height < 0.75 : false;

  // Get container class based on orientation
  const containerClass = `${styles.videoContainer} ${
    isVerticalVideo ? styles.verticalVideo : styles.horizontalVideo
  } ${showQualityMenu ? styles.controlsActive : ""} ${className}`;

  // Setup video player and HLS
  useEffect(() => {
    if (!videoRef.current) return;

    // debugService.info("Initializing video player", { url: video.url });

    // Cleanup function
    const cleanup = () => {
      // debugService.info("Cleaning up HLS instance");
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };

    // Handle HLS video
    if (video.url.includes(".m3u8")) {
      if (Hls.isSupported()) {
        // debugService.info("HLS.js is supported, creating instance");

        cleanup();

        // Create a new HLS instance with optimized config
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
          maxBufferLength: 30,
          fragLoadingMaxRetry: 6,
          manifestLoadingMaxRetry: 6,
          levelLoadingMaxRetry: 6,
          maxBufferSize: 60 * 1000 * 1000,
          maxBufferHole: 0.5,
          startLevel: -1,
          capLevelToPlayerSize: true,
          xhrSetup: (xhr) => {
            xhr.withCredentials = false;
          },
        });

        hlsRef.current = hls;

        // Event handlers for HLS.js
        hls.on(Hls.Events.MEDIA_ATTACHED, () => {
          // debugService.info("HLS.js: Media element attached");
        });

        hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
          // debugService.info("HLS.js: Manifest parsed", {
          //   levels: data.levels.length,
          //   firstLevel: data.firstLevel,
          //   audioTracks: data.audioTracks?.length || 0,
          // });

          // Setup quality levels if available
          if (data.levels.length > 1) {
            const levels = data.levels.map((level, index) => ({
              value: index,
              label: level.height ? `${level.height}p` : `Quality ${index + 1}`,
            }));

            // Add auto quality option
            levels.unshift({ value: -1, label: "Auto" });
            setQualityLevels(levels);
            setCurrentQuality(-1); // Default to auto
          }

          setLoading(false);

          if (onReady) onReady();

          // Start playback if autoplay is enabled
          if (autoPlay && videoRef.current) {
            // debugService.info("Attempting autoplay");
            videoRef.current.play().catch((e) => {
              console.log("Autoplay failed", { error: e });
              // debugService.error("Autoplay failed", { error: e });
            });
          }
        });

        // Monitor for errors
        hls.on(Hls.Events.ERROR, (_, data) => {
          // debugService.error("HLS.js error", {
          //   type: data.type,
          //   details: data.details,
          //   fatal: data.fatal,
          // });

          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                // debugService.info("Attempting to recover from network error");
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                // debugService.info("Attempting to recover from media error");
                hls.recoverMediaError();
                break;
              default:
                setError(new Error(`HLS fatal error: ${data.details}`));
                if (onError)
                  onError(new Error(`HLS fatal error: ${data.details}`));
                break;
            }
          }
        });

        // Handle level switching
        hls.on(Hls.Events.LEVEL_SWITCHED, (_, data) => {
          const currentLevel = data.level;
          // debugService.info(`Switched to quality level: ${currentLevel}`);

          // Only update if user hasn't manually selected (we use -1 for auto)
          if (currentQuality === -1) {
            setCurrentQuality(currentLevel);
          }
        });

        // Load the source and attach to video element
        // debugService.info("Loading HLS source", { url: video.url });
        hls.loadSource(video.url);
        hls.attachMedia(videoRef.current);

        // Set up video element event handlers
        if (videoRef.current) {
          videoRef.current.onerror = (e) => {
            const errorMessage = `Video element error: ${
              videoRef.current?.error?.message || "Unknown error"
            }`;
            console.log(errorMessage, { event: e });
            // debugService.error(errorMessage, { event: e });
            setError(new Error(errorMessage));
            if (onError) onError(new Error(errorMessage));
          };

          videoRef.current.onplaying = () => {
            // debugService.info("Video element: playing event");
          };

          videoRef.current.onwaiting = () => {
            // debugService.info("Video element: waiting for data");
          };

          videoRef.current.onstalled = () => {
            // debugService.info("Video element: stalled");
          };

          videoRef.current.onended = () => {
            // debugService.info("Video element: playback ended");
            if (onEnded) onEnded();
          };
        }
      } else if (
        videoRef.current.canPlayType("application/vnd.apple.mpegurl")
      ) {
        // Native HLS support (Safari)
        // debugService.info("Using native HLS support");
        videoRef.current.src = video.url;

        videoRef.current.onloadedmetadata = () => {
          // debugService.info("Native HLS: metadata loaded");
          setLoading(false);
          if (onReady) onReady();

          if (autoPlay) {
            videoRef.current?.play().catch((e) => {
              console.log("Native HLS autoplay failed", { error: e });
              // debugService.error("Native HLS autoplay failed", { error: e });
            });
          }
        };

        videoRef.current.onerror = (e) => {
          const errorMessage = `Native HLS error: ${
            videoRef.current?.error?.message || "Unknown error"
          }`;
          console.log(errorMessage, { event: e });
          // debugService.error(errorMessage, { event: e });
          setError(new Error(errorMessage));
          if (onError) onError(new Error(errorMessage));
        };
      } else {
        // No HLS support
        const errorMessage = "This browser does not support HLS playback";
        // debugService.error(errorMessage);
        setError(new Error(errorMessage));
        if (onError) onError(new Error(errorMessage));
      }
    } else {
      // Regular video (not HLS)
      // debugService.info("Loading regular video", { url: video.url });
      videoRef.current.src = video.url;

      videoRef.current.onloadeddata = () => {
        // debugService.info("Regular video: data loaded");
        setLoading(false);
        if (onReady) onReady();

        if (autoPlay) {
          videoRef.current?.play().catch((e) => {
            console.log("Regular video autoplay failed", { error: e });
            // debugService.error("Regular video autoplay failed", { error: e });
          });
        }
      };
    }

    return cleanup;
  }, [video.url, autoPlay, muted, loop, onError, onReady, onEnded]);

  // Предотвращаем клик на видео при открытом меню
  const handleVideoClick = useCallback(
    (event: MouseEvent) => {
      if (showQualityMenu && preventVideoClickRef.current) {
        event.preventDefault();
        event.stopPropagation();
        preventVideoClickRef.current = false; // Сбрасываем флаг после использования
        return false; // Предотвращаем действие по умолчанию
      }
      return true;
    },
    [showQualityMenu]
  );

  // Закрытие меню при клике вне его
  const handleClickOutside = useCallback((event: MouseEvent) => {
    // Проверяем, был ли клик вне меню и вне кнопки качества
    if (
      qualityMenuRef.current &&
      qualityButtonRef.current &&
      !qualityMenuRef.current.contains(event.target as Node) &&
      !qualityButtonRef.current.contains(event.target as Node)
    ) {
      // Если клик был на видео, устанавливаем флаг для предотвращения действия
      if (videoRef.current && videoRef.current.contains(event.target as Node)) {
        preventVideoClickRef.current = true;
      }

      setShowQualityMenu(false);
    }
  }, []);

  // Устанавливаем обработчик клика и обработчик предотвращения клика на видео
  useEffect(() => {
    // Добавляем обработчик только когда меню открыто
    if (showQualityMenu) {
      document.addEventListener("mousedown", handleClickOutside);

      // Добавляем обработчик для предотвращения клика на видео
      if (videoRef.current) {
        videoRef.current.addEventListener("click", handleVideoClick);
      }
    } else {
      document.removeEventListener("mousedown", handleClickOutside);

      // Удаляем обработчик, когда меню закрыто
      if (videoRef.current) {
        videoRef.current.removeEventListener("click", handleVideoClick);
      }
    }

    // Очистка при размонтировании
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (videoRef.current) {
        videoRef.current.removeEventListener("click", handleVideoClick);
      }
    };
  }, [showQualityMenu, handleClickOutside, handleVideoClick]);

  // Handle quality change
  const handleQualityChange = (level: number) => {
    if (!hlsRef.current) return;

    // debugService.info(`Manual quality change to level: ${level}`);
    hlsRef.current.currentLevel = level;
    setCurrentQuality(level);
    setShowQualityMenu(false);
  };

  // Handle retry attempt
  const handleRetry = () => {
    if (!videoRef.current) return;

    // debugService.info("Retry requested");
    setError(null);
    setLoading(true);

    // Recreate the player
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    // Reload the video with a slight delay
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.load();

        if (video.url.includes(".m3u8")) {
          if (Hls.isSupported()) {
            const hls = new Hls();
            hlsRef.current = hls;
            hls.loadSource(video.url);
            hls.attachMedia(videoRef.current);
          } else {
            videoRef.current.src = video.url;
          }
        } else {
          videoRef.current.src = video.url;
        }

        // Try to play after a short delay
        setTimeout(() => {
          if (videoRef.current && autoPlay) {
            videoRef.current.play().catch((e) => {
              console.log("Retry autoplay failed", { error: e });
              // debugService.error("Retry autoplay failed", { error: e });
            });
          }
        }, 200);
      }
    }, 100);
  };

  // Get poster URL
  const getPosterUrl = () => {
    return ThumbnailGenerator.getVideoThumbnailUrl(video);
  };

  // Toggle quality menu
  const toggleQualityMenu = (e: React.MouseEvent) => {
    e.stopPropagation(); // Предотвращаем всплытие события
    setShowQualityMenu(!showQualityMenu);
    // При открытии меню качества, принудительно показываем панель управления
    if (!showQualityMenu) {
      setControlsVisible(true);
    }
  };

  // Обрабатываем перемещение мыши над видео
  const handleMouseOver = () => {
    setControlsVisible(true);
  };

  const handleMouseOut = () => {
    // Скрываем панель управления, только если меню качества не открыто
    if (!showQualityMenu) {
      setControlsVisible(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className={containerClass}
      onMouseEnter={handleMouseOver}
      onMouseLeave={handleMouseOut}
    >
      {loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
        </div>
      )}

      {error && (
        <div className={styles.errorOverlay}>
          <p>Error loading video</p>
          <p className={styles.errorMessage}>{error.message}</p>
          <button onClick={handleRetry} className={styles.retryButton}>
            Retry
          </button>
        </div>
      )}

      {/* Невидимый оверлей, который появляется только при открытом меню,
          для перехвата кликов по видео */}
      {showQualityMenu && (
        <div
          className={styles.menuOverlay}
          onClick={() => setShowQualityMenu(false)}
        />
      )}

      <video
        ref={videoRef}
        className={styles.videoElement}
        poster={getPosterUrl()}
        controls
        playsInline
        muted={muted}
        loop={loop}
        crossOrigin="anonymous"
      >
        {!video.url.includes(".m3u8") && (
          <source src={video.url} type="video/mp4" />
        )}
        Your browser does not support video playback.
      </video>

      {/* Quality selector - only show if we have multiple quality levels */}
      {qualityLevels.length > 1 && (
        <div
          className={`${styles.controlsOverlay} ${
            showQualityMenu ? styles.menuOpen : ""
          } ${showQualityMenu || controlsVisible ? styles.active : ""}`}
        >
          <button
            ref={qualityButtonRef}
            className={styles.qualityButton}
            onClick={toggleQualityMenu}
            title="Quality settings"
          >
            HD
          </button>

          {showQualityMenu && (
            <div
              ref={qualityMenuRef}
              className={styles.qualityMenu}
              onClick={(e) => e.stopPropagation()} // Предотвращаем всплытие события клика
            >
              {qualityLevels.map((level) => (
                <button
                  key={level.value}
                  className={`${styles.qualityOption} ${
                    currentQuality === level.value ? styles.activeQuality : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation(); // Предотвращаем всплытие события
                    handleQualityChange(level.value);
                  }}
                >
                  {level.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
